import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
// react icons
import { useDispatch } from 'react-redux';
import { GiTakeMyMoney } from 'react-icons/gi';
import { MdOutlinePermContactCalendar, MdRemoveDone } from 'react-icons/md';
import { RiCalendarEventFill } from 'react-icons/ri';
import { VscCheckAll } from 'react-icons/vsc';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import aadharFrontDummy from './aadharFrontDummy.jpg';
import { fDate } from '../../utils/formatTime';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import { CommonApiforJourney } from '../services/RecommendationApi';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDisburseSelector from '../../store/Slice/Disburse/DisburseSelector';
import { getDecisionInfoData } from '../../store/Slice/Disburse/DisburseSlice';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  margin: '0 3px',
  [`&:hover`]: {
    color: theme.palette.primary.main
  }
}));

function ProcessDisburse() {
  // const { rowId } = useParams();
  const navigate = useNavigate();
  const [documentPreview, setDocumentPreview] = useState({
    popup: false,
    imgPreview: aadharFrontDummy
  });

  const [rejReasonState, setRejReasonState] = useState({
    popup: false,
    type: null,
    rejectionMsg: null,
    idd: null
  });

  const [openLoader, setOpenLoader] = useState(false);
  const [openApiError, setOpenApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [subPageStatusMap, setSubPageStatusMap] = useState({});

  const dispatch = useDispatch();
  const location = useLocation();
  const rowId = location.state || {};

  const { LoginData } = useLoginSelector();
  const { DisburseDecisionInfo } = useDisburseSelector();

  const getDisbirseViewAPi = useCallback(() => {
    const finalObject = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      applicationId: rowId?.applicationId
    };
    dispatch(getDecisionInfoData(finalObject));
  }, [LoginData, rowId]);

  useEffect(() => {
    getDisbirseViewAPi();
  }, [getDisbirseViewAPi]);

  useEffect(() => {
    // Retrieve and parse the data from sessionStorage
    const storedData = sessionStorage.getItem('reportTypeId');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const statusMap = {};
      parsedData[0].accessSubPagesMappings.forEach((subPage) => {
        statusMap[subPage.subPageCode] = subPage.isEnable;
      });
      setSubPageStatusMap(statusMap);
    }
  }, []);

  // Function to check if a subPageCode is enabled
  const isSubPageEnabled = (subPageCode) => subPageStatusMap[subPageCode] || false;

  const disburseApiFire = async (type, condition) => {
    const token = LoginData?.jwt;

    const basePayload = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };

    const payload = { ...basePayload };
    const methodName = 'POST';
    let endpoint = '';

    switch (type) {
      case 'saveDecisionInfo': {
        const prevData = rejReasonState;
        const decisionInfo = DisburseDecisionInfo?.disbursementDecisionInfoResponseModel || {};

        const isDisbursement = prevData?.type === 'Disbursement';
        const isRejected = condition === 'reject';

        if (isDisbursement) {
          Object.assign(payload, {
            disbursementAcceptance: !isRejected,
            disbursementId: prevData?.idd,
            disburseCell: rowId
          });
        } else {
          Object.assign(payload, {
            encashmentAcceptance: !isRejected,
            encashmentId: prevData?.idd,
            disbursedAmount: decisionInfo?.disbursedAmount ?? null,
            disbursedDate: decisionInfo?.disbursedDate ?? null,
            disburseCell: rowId
          });
        }

        endpoint = 'disbursed/saveDecisionInfo';
        break;
      }

      case 'getImageUrl': {
        try {
          const parsedData = JSON.parse(condition);
          Object.assign(payload, {
            applicationId: parsedData?.applicationId,
            fileName: parsedData?.fileName
          });
          endpoint = 'file/downloadFile';
        } catch (err) {
          console.error('Invalid condition JSON for getImageUrl', err);
          return;
        }
        break;
      }

      default:
        console.warn(`Unhandled API type: ${type}`);
        return;
    }

    // ✅ Final API call
    disburseApiFireResponse(type, methodName, endpoint, JSON.stringify(payload), token, '');
  };

  const disburseApiFireResponse = async (
    type,
    method,
    endpoint,
    credentials,
    authToken,
    frmData
  ) => {
    setOpenLoader(true);
    const responseData = await CommonApiforJourney(
      method,
      endpoint,
      credentials,
      authToken,
      frmData
    );

    if (responseData.status === 'success' || responseData.action_status === 'success') {
      setOpenLoader(false);
      if (type === 'saveDecisionInfo') {
        if (rejReasonState.rejectionMsg === 'approve') {
          setOpenApiError(true);
          setErrorMessage(`${rejReasonState.type} Approved`);
          getDisbirseViewAPi();
        } else {
          navigate('/disburse');
        }
        setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null });
      }
    } else if (responseData.type === 'application/octet-stream') {
      setOpenLoader(false);
      const blobData = URL.createObjectURL(responseData);
      if (type === 'getImageUrl') {
        setDocumentPreview({ popup: true, imgPreview: blobData });
      }
    } else {
      setOpenLoader(false);
      setOpenApiError(true);
      setErrorMessage('Something went wrong !');
    }
  };

  return (
    <Page title="Partner Management System || Disburse">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Process" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          {isValidValue(DisburseDecisionInfo.disbursementDecisionInfoResponseModel) ? (
            <>
              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                Disbursement
              </Typography>
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                {isSubPageEnabled('DISBURSE_APPROVE') && (
                  <Box className="extraTopIcon">
                    <Tooltip title="Approve" placement="top" arrow>
                      <StyledIconButton
                        onClick={() => {
                          const prevDatas = rejReasonState;
                          prevDatas.popup = false;
                          prevDatas.type = 'Disbursement';
                          prevDatas.rejectionMsg = 'approve';
                          prevDatas.idd =
                            DisburseDecisionInfo?.disbursementDecisionInfoResponseModel?.disbursementId;
                          setRejReasonState(prevDatas);
                          disburseApiFire('saveDecisionInfo', 'approve');
                        }}
                        className="squareIconButton squareIconButtonFilled"
                        classes={{ disabled: 'disabledBtn' }}
                        disabled={
                          isSubPageEnabled('DISBURSE_APPROVE') &&
                          DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                            ?.acceptanceStatus
                        }
                      >
                        <VscCheckAll size={14} />
                      </StyledIconButton>
                    </Tooltip>
                    <Tooltip title="Reject" placement="top" arrow>
                      <StyledIconButton
                        className="squareIconButton squareIconButtonFilled ml-1"
                        classes={{ disabled: 'disabledBtn' }}
                        disabled={
                          isSubPageEnabled('DISBURSE_REJECT') &&
                          DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                            ?.acceptanceStatus
                        }
                        onClick={() => {
                          setRejReasonState({
                            popup: true,
                            type: 'Disbursement',
                            rejectionMsg: null,
                            idd: DisburseDecisionInfo?.disbursementDecisionInfoResponseModel
                              ?.disbursementId
                          });
                        }}
                      >
                        <MdRemoveDone size={14} />
                      </StyledIconButton>
                    </Tooltip>
                  </Box>
                )}

                <Grid container spacing={2} mb={3}>
                  <Grid item xs={3}>
                    <Box className="displayFlex">
                      <GiTakeMyMoney className="viewLabelIcon" size={24} />
                      <Box>
                        <Typography variant="body2" className="labelText">
                          Disbursed Amount
                        </Typography>
                        <Typography variant="subtitle2" className="labelValue">
                          {DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                            .disbursedAmount
                            ? DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                                .disbursedAmount
                            : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box className="displayFlex">
                      <RiCalendarEventFill className="viewLabelIcon" size={24} />
                      <Box>
                        <Typography variant="body2" className="labelText">
                          Disbursed Date
                        </Typography>
                        <Typography variant="subtitle2" className="labelValue">
                          {DisburseDecisionInfo.disbursementDecisionInfoResponseModel.disbursedDate
                            ? fDate(
                                DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                                  .disbursedDate
                              )
                            : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box className="displayFlex">
                      <MdOutlinePermContactCalendar className="viewLabelIcon" size={24} />
                      <Box>
                        <Typography variant="body2" className="labelText">
                          Submited By
                        </Typography>
                        <Typography variant="subtitle2" className="labelValue">
                          {normalizeValue(
                            DisburseDecisionInfo.disbursementDecisionInfoResponseModel.submittedBy,
                            `${NOT_AVAILABLE}`
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box className="displayFlex">
                      <RiCalendarEventFill className="viewLabelIcon" size={24} />
                      <Box>
                        <Typography variant="body2" className="labelText">
                          Submitted On
                        </Typography>
                        <Typography variant="subtitle2" className="labelValue">
                          {DisburseDecisionInfo.disbursementDecisionInfoResponseModel.submittedOn
                            ? fDate(
                                DisburseDecisionInfo.disbursementDecisionInfoResponseModel
                                  .submittedOn
                              )
                            : `${NOT_AVAILABLE}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box className="displayFlex flexWrap">
                  {DisburseDecisionInfo?.disbursementDecisionInfoResponseModel
                    ?.disbursementDisburseDocumentsDetailsModels &&
                    DisburseDecisionInfo?.disbursementDecisionInfoResponseModel?.disbursementDisburseDocumentsDetailsModels?.map(
                      (imgItem) => (
                        <Box
                          className="uploadFilePreview uploadFilePreviewGreen"
                          mb={2}
                          onClick={() => {
                            const payloadObj = {
                              applicationId: DisburseDecisionInfo.applicationId,
                              fileName: imgItem.savedName
                            };
                            disburseApiFire('getImageUrl', JSON.stringify(payloadObj));
                          }}
                        >
                          <RemoveRedEyeIcon className="ImageViewIcon" />
                          <Typography variant="body2" className="docName">
                            {imgItem.fileName.split('.')[0]}
                          </Typography>
                        </Box>
                      )
                    )}
                </Box>
              </Box>
            </>
          ) : (
            ''
          )}

          {DisburseDecisionInfo.encashmentDecisionInfoResponseModelList &&
            DisburseDecisionInfo?.encashmentDecisionInfoResponseModelList?.map((itemL) => (
              <>
                <Typography variant="body1" className="headingLineUp headingLineUpOut">
                  Encashment
                </Typography>
                <Box className="viewLeadCard cardTopMinusMargin" style={{ marginBottom: '20px' }}>
                  {isSubPageEnabled('DISBURSE_APPROVE') && (
                    <Box className="extraTopIcon">
                      <Tooltip title="Approve" placement="top" arrow>
                        <StyledIconButton
                          onClick={() => {
                            const prevDatas = rejReasonState;
                            prevDatas.popup = false;
                            prevDatas.type = 'Encashment';
                            prevDatas.rejectionMsg = 'approve';
                            prevDatas.idd = itemL?.encashmentId;
                            setRejReasonState(prevDatas);
                            disburseApiFire('saveDecisionInfo', 'approve');
                          }}
                          className="squareIconButton squareIconButtonFilled"
                          classes={{ disabled: 'disabledBtn' }}
                          disabled={itemL?.acceptanceStatus}
                        >
                          <VscCheckAll size={14} />
                        </StyledIconButton>
                      </Tooltip>
                      <Tooltip title="Reject" placement="top" arrow>
                        <StyledIconButton
                          className="squareIconButton squareIconButtonFilled ml-1"
                          classes={{ disabled: 'disabledBtn' }}
                          disabled={itemL?.acceptanceStatus}
                          onClick={() => {
                            setRejReasonState({
                              popup: true,
                              type: 'Encashment',
                              rejectionMsg: null,
                              idd: itemL?.encashmentId
                            });
                          }}
                        >
                          <MdRemoveDone size={14} />
                        </StyledIconButton>
                      </Tooltip>
                    </Box>
                  )}

                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={3}>
                      <Box className="displayFlex">
                        <GiTakeMyMoney className="viewLabelIcon" size={24} />
                        <Box>
                          <Typography variant="body2" className="labelText">
                            Encashment Amount
                          </Typography>
                          <Typography variant="subtitle2" className="labelValue">
                            {itemL.encashmentAmount ? itemL.encashmentAmount : `${NOT_AVAILABLE}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box className="displayFlex">
                        <RiCalendarEventFill className="viewLabelIcon" size={24} />
                        <Box>
                          <Typography variant="body2" className="labelText">
                            Encashment Date
                          </Typography>
                          <Typography variant="subtitle2" className="labelValue">
                            {itemL.encashmentDate
                              ? fDate(itemL.encashmentDate)
                              : `${NOT_AVAILABLE}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box className="displayFlex">
                        <MdOutlinePermContactCalendar className="viewLabelIcon" size={24} />
                        <Box>
                          <Typography variant="body2" className="labelText">
                            Submited By
                          </Typography>
                          <Typography variant="subtitle2" className="labelValue">
                            {itemL.submittedBy ? itemL.submittedBy : `${NOT_AVAILABLE}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box className="displayFlex">
                        <RiCalendarEventFill className="viewLabelIcon" size={24} />
                        <Box>
                          <Typography variant="body2" className="labelText">
                            Submitted On
                          </Typography>
                          <Typography variant="subtitle2" className="labelValue">
                            {itemL.submittedOn ? fDate(itemL.submittedOn) : `${NOT_AVAILABLE}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box className="displayFlex flexWrap">
                    {itemL.encashmentDisburseDocumentsDetailsModels &&
                      itemL.encashmentDisburseDocumentsDetailsModels.map((imgItem) => (
                        <Box
                          className="uploadFilePreview uploadFilePreviewGreen"
                          mb={2}
                          onClick={() => {
                            const payloadObj = {
                              applicationId: DisburseDecisionInfo.applicationId,
                              fileName: imgItem.savedName
                            };
                            disburseApiFire('getImageUrl', JSON.stringify(payloadObj));
                          }}
                        >
                          <RemoveRedEyeIcon className="ImageViewIcon" />
                          <Typography variant="body2" className="docName">
                            {imgItem.fileName.split('.')[0]}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              </>
            ))}
        </Box>

        <Dialog
          open={rejReasonState.popup}
          onClose={(e) => {
            setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null });
          }}
          fullWidth
        >
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Rejection Reason</Typography>
            <IconButton
              onClick={() =>
                setRejReasonState({ popup: false, type: null, rejectionMsg: null, idd: null })
              }
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              multiline
              value={rejReasonState.rejectionMsg}
              onChange={(e) => {
                const prevDatas = rejReasonState;
                prevDatas.rejectionMsg = e.target.value;
                setRejReasonState({ ...rejReasonState, prevDatas });
              }}
              rows={5}
              placeholder="Write Rejection Reason"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => disburseApiFire('saveDecisionInfo', 'reject')}
              variant="contained"
              className="mainBtn"
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={documentPreview.popup}
          onClose={(e) => setDocumentPreview({ popup: false, imgPreview: null })}
          fullWidth
        >
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Document Preview</Typography>
            <IconButton
              onClick={() => setDocumentPreview({ popup: false, imgPreview: null })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <img src={documentPreview.imgPreview} alt="Aadhar Front" className="" />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDocumentPreview({ popup: false, imgPreview: null })}
              variant="contained"
              className="mainBtn"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <ApiErrorMessage
          openApiError={openApiError}
          setOpenApiError={setOpenApiError}
          errorMessage={errorMessage}
        />
        <Loader openLoader={openLoader} />
      </Container>
    </Page>
  );
}
export default ProcessDisburse;
